import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Send, Paperclip, X, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { TicketAttachment } from '@/components/tickets/TicketAttachment';
import { ticketSenderLabel } from '@/components/tickets/senderLabel';
import { apiErrorMessage } from '@/lib/api-error';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { intlLocale } from './adminLocale';

const MAX_FILE_SIZE = 15 * 1024 * 1024;

type TicketResource = components['schemas']['TicketResource'];

const statusLabels = (): Record<string, string> => ({
  new: i18n.t('adm_tickets.st_new'), reviewing: i18n.t('adm_tickets.st_reviewing'), resolved: i18n.t('adm_tickets.st_resolved'), closed: i18n.t('adm_tickets.st_closed'),
});
const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800', reviewing: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800', closed: 'bg-muted text-muted-foreground',
};
const priorityLabels = (): Record<string, string> => ({
  low: i18n.t('adm_tickets.pr_low'), medium: i18n.t('adm_tickets.pr_medium'), high: i18n.t('adm_tickets.pr_high'),
});

const builtinModules = (): Record<string, string> => ({
  hisob_app: i18n.t('adm_ticketDetail.mod_hisob_app'), customers: i18n.t('adm_ticketDetail.mod_customers'), murabaha: i18n.t('adm_ticketDetail.mod_murabaha'),
  inventory: i18n.t('adm_ticketDetail.mod_inventory'), products: i18n.t('adm_ticketDetail.mod_products'), sales: i18n.t('adm_ticketDetail.mod_sales'),
  pricing: i18n.t('adm_ticketDetail.mod_pricing'), branches: i18n.t('adm_ticketDetail.mod_branches'), reports: i18n.t('adm_ticketDetail.mod_reports'), pos: i18n.t('adm_ticketDetail.mod_pos'),
});

export default function AdminTicketDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const ticketId = Number(id);
  const navigate = useNavigate();
  const { admin, hasRole } = useAdminAuth();
  const queryClient = useQueryClient();
  const [newMessage, setNewMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('adm_ticketDetail.fileTooLarge'));
      return;
    }
    setSelectedFile(file);
  };

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['/admin/tickets/{ticket}', ticketId],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/admin/tickets/{ticket}', {
        params: { path: { ticket: ticketId } },
      });
      if (error) throw error;
      return data.data as TicketResource;
    },
    enabled: !!ticketId,
    refetchInterval: 10_000,
  });

  // Status updates are owner/support only (UpdateTicketStatusRequest); a director counts as owner.
  const canChangeStatus = hasRole('platform_owner', 'support_admin');
  const canManage = !!admin;

  const sendMutation = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      if (newMessage.trim()) body.append('message', newMessage.trim());
      if (selectedFile) body.append('attachment', selectedFile);
      const { error } = await apiClient.POST('/admin/tickets/{ticket}/messages', {
        params: { path: { ticket: ticketId } },
        body: body as unknown as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/admin/tickets/{ticket}', ticketId] });
      setNewMessage('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (e) => toast.error(apiErrorMessage(e, t('adm_ticketDetail.sendFailed'))),
  });

  const statusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const { error } = await apiClient.PATCH('/admin/tickets/{ticket}', {
        params: { path: { ticket: ticketId } },
        body: { status: newStatus as 'new' | 'reviewing' | 'resolved' | 'closed' },
      });
      if (error) throw error;
    },
    onSuccess: (_data, newStatus) => {
      queryClient.invalidateQueries({ queryKey: ['/admin/tickets/{ticket}', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['/admin/tickets'] });
      toast.success(t('adm_ticketDetail.statusChanged', { status: statusLabels()[newStatus] }));
    },
    onError: (e) => toast.error(apiErrorMessage(e, t('adm_common.errorGeneric'))),
  });

  const messageCount = ticket?.messages?.length ?? 0;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messageCount]);

  if (isLoading) return <div className="p-6 text-muted-foreground">{t('adm_common.loading')}</div>;
  if (!ticket) return <div className="p-6 text-muted-foreground">{t('adm_ticketDetail.notFound')}</div>;

  const messages = ticket.messages ?? [];

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/tickets')}>
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-foreground truncate">{ticket.subject}</h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-mono">{ticket.ticket_number}</span> · {ticket.merchant?.name || '—'} · {new Date(ticket.created_at).toLocaleDateString(intlLocale())}
          </p>
        </div>
        <Badge variant="outline" className={statusColors[ticket.status] || ''}>
          {statusLabels()[ticket.status] || ticket.status}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1 min-h-0">
        <Card className="lg:col-span-1 h-fit">
          <CardHeader className="pb-2"><CardTitle className="text-sm">{t('adm_ticketDetail.info')}</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{t('adm_tickets.priority')}</span><span>{priorityLabels()[ticket.priority] || ticket.priority}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t('adm_tickets.status')}</span><span>{statusLabels()[ticket.status]}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t('adm_tickets.creator')}</span><span>{ticket.created_by_user?.name || '—'}</span></div>
            {ticket.body && (
              <div>
                <p className="text-muted-foreground mb-1">{t('adm_ticketDetail.description')}</p>
                <p className="text-foreground text-xs whitespace-pre-wrap">{ticket.body}</p>
              </div>
            )}
            {ticket.module_code && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('adm_ticketDetail.module')}</span>
                <span>{builtinModules()[ticket.module_code] || ticket.module_code}</span>
              </div>
            )}
            {ticket.product && (
              <div>
                <p className="text-muted-foreground">{t('adm_ticketDetail.product')}</p>
                <p className="text-xs">{ticket.product.name} ({ticket.product.sku})</p>
              </div>
            )}
            {ticket.customer && (
              <div>
                <p className="text-muted-foreground">{t('adm_ticketDetail.customer')}</p>
                <p className="text-xs">{ticket.customer.full_name} — {ticket.customer.phone}</p>
              </div>
            )}
            {canChangeStatus && (
              <div className="pt-2 border-t">
                <p className="text-muted-foreground mb-2 text-xs">{t('adm_ticketDetail.changeStatus')}</p>
                <Select value={ticket.status} onValueChange={(v) => statusMutation.mutate(v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels()).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 flex flex-col min-h-[400px]">
          <CardHeader className="pb-2"><CardTitle className="text-sm">{t('adm_ticketDetail.chat')}</CardTitle></CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto space-y-3 mb-3 pr-1">
              {messages.length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-8">{t('adm_ticketDetail.noMessages')}</p>
              )}
              {messages.map(msg => {
                const isAdmin = msg.sender_id === admin?.id;
                return (
                  <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${isAdmin ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                      <p className="text-[10px] font-medium mb-1 opacity-75">
                        {ticketSenderLabel(msg)}
                      </p>
                      <p>{msg.message}</p>
                      {msg.has_attachment && msg.attachment_name && (
                        <TicketAttachment
                          ticketId={ticketId}
                          messageId={msg.id}
                          name={msg.attachment_name}
                          admin
                          className={isAdmin ? 'text-primary-foreground' : ''}
                        />
                      )}
                      <p className={`text-[10px] mt-1 ${isAdmin ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                        {new Date(msg.created_at).toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            {canManage && (
              <div className="space-y-2">
                {selectedFile && (
                  <div className="flex items-center gap-2 text-xs bg-muted rounded px-2 py-1.5">
                    {selectedFile.type.startsWith('image/') ? <ImageIcon size={14} /> : <FileText size={14} />}
                    <span className="truncate flex-1">{selectedFile.name}</span>
                    <span className="text-muted-foreground">{(selectedFile.size / 1024 / 1024).toFixed(1)} MB</span>
                    <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                      <X size={14} className="text-muted-foreground hover:text-foreground" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                    onChange={handleFileSelect}
                  />
                  <Button variant="outline" size="icon" onClick={() => fileInputRef.current?.click()} type="button">
                    <Paperclip size={16} />
                  </Button>
                  <Input
                    placeholder={t('adm_ticketDetail.messagePlaceholder')}
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMutation.mutate()}
                    disabled={sendMutation.isPending}
                  />
                  <Button size="icon" onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending || (!newMessage.trim() && !selectedFile)}>
                    <Send size={16} />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
